import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  Timestamp,
  where
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBriefcase,
  faBuilding,
  faCalendarDay,
  faClipboardList,
  faCoins,
  faEnvelope,
  faIdCard,
  faPhone,
  faShieldHalved,
  faUser,
  faUserClock
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { app } from '../../firebase';
import '../dash.css';
import {
  formatDateTime,
  formatKes,
  getDateTimeInputValue,
  getEmployeeSalaryForPeriod,
  getMonthKey,
  getMonthLabel
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const SECTION_CONFIG = {
  leaveRequests: {
    title: 'Leave requests',
    eyebrow: 'Employee records',
    description: 'Review and add leave requests with dates, approval status, and supporting notes.',
    emptyTitle: 'No leave requests yet',
    emptyMessage: 'Leave requests for this employee will appear here once they are recorded.',
    searchPlaceholder: 'Search leave type, status, approver, or reason...'
  },
  salaryPayments: {
    title: 'Salary payments',
    eyebrow: 'Employee payroll',
    description: 'Record salary payments in a dedicated payroll page and keep the full employee payment trail visible.',
    emptyTitle: 'No salary payments yet',
    emptyMessage: 'Salary payments recorded for this employee will appear in this table.',
    searchPlaceholder: 'Search period, method, reference, or notes...'
  },
  activityLogs: {
    title: 'Activity logs',
    eyebrow: 'Employee audit trail',
    description: 'Track recent admin actions connected to this employee profile and related management pages.',
    emptyTitle: 'No activity logs linked yet',
    emptyMessage: 'Employee-specific logs will start appearing here as the profile workflows are used.',
    searchPlaceholder: 'Search admin, action, details, or page...'
  },
  teachingSessions: {
    title: 'Teaching sessions',
    eyebrow: 'Employee teaching',
    description: 'View class assignments and teaching sessions where this employee is the assigned instructor.',
    emptyTitle: 'No teaching sessions found',
    emptyMessage: 'If this employee is teaching classes, those records will appear here.',
    searchPlaceholder: 'Search class, campus, schedule, or status...'
  },
  checkIns: {
    title: 'Check-in records',
    eyebrow: 'Employee attendance',
    description: 'Capture manual attendance entries, shift notes, and check-in / check-out history for this employee.',
    emptyTitle: 'No check-in records yet',
    emptyMessage: 'Check-in and attendance records will appear here after the first entry is saved.',
    searchPlaceholder: 'Search location, status, notes, or shift...'
  },
  disciplinaryRecords: {
    title: 'Disciplinary records',
    eyebrow: 'Employee conduct',
    description: 'Maintain warnings, disciplinary actions, severity, and follow-up notes in a dedicated history.',
    emptyTitle: 'No disciplinary records yet',
    emptyMessage: 'Warnings and disciplinary records for this employee will appear here once added.',
    searchPlaceholder: 'Search type, severity, issued by, or summary...'
  }
};

const SALARY_METHOD_OPTIONS = ['Bank Transfer', 'MPESA', 'Cash', 'Cheque', 'Other'];

const createSectionFormState = (section) => {
  switch (section) {
    case 'leaveRequests':
      return {
        leaveType: 'Annual Leave',
        startDate: '',
        endDate: '',
        status: 'Pending',
        approverName: '',
        reason: ''
      };
    case 'checkIns':
      return {
        checkInAt: getDateTimeInputValue(),
        checkOutAt: '',
        attendanceStatus: 'Present',
        location: '',
        shiftLabel: '',
        notes: ''
      };
    case 'disciplinaryRecords':
      return {
        recordType: 'Warning',
        severity: 'Low',
        incidentDate: '',
        status: 'Active',
        issuedBy: '',
        summary: '',
        actionTaken: ''
      };
    default:
      return {};
  }
};

const buildSalaryFormState = () => ({
  amount: '',
  paymentDate: getDateTimeInputValue(),
  method: SALARY_METHOD_OPTIONS[0],
  reference: '',
  notes: ''
});

const normalizeEmployeeStatus = (status) => String(status || 'Active').trim();
const getEmployeeStatusClass = (status) => normalizeEmployeeStatus(status).toLowerCase();

const toDateObject = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toTimestampNumber = (value) => {
  const dateValue = toDateObject(value);
  return dateValue ? dateValue.getTime() : 0;
};

const matchesSearch = (queryText, values) => {
  if (!queryText) return true;
  const haystack = values.filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(queryText);
};

const calculateDateRangeDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

function EmployeeSectionComp({ section }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const db = getFirestore(app);
  const auth = getAuth();
  const hasLoggedViewRef = useRef(false);

  const config = SECTION_CONFIG[section];
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionForm, setSectionForm] = useState(createSectionFormState(section));
  const [salaryForm, setSalaryForm] = useState(buildSalaryFormState());
  const [periodKey, setPeriodKey] = useState(getMonthKey());

  const loadEmployeeSection = useCallback(async () => {
    if (!id || !config) {
      setEmployee(null);
      setRecords([]);
      return;
    }

    const employeeSnap = await getDoc(doc(db, 'Employees', id));
    if (!employeeSnap.exists()) {
      setEmployee(null);
      setRecords([]);
      return;
    }

    const employeeRecord = { id: employeeSnap.id, ...employeeSnap.data() };
    setEmployee(employeeRecord);

    let nextRecords = [];

    if (section === 'salaryPayments') {
      const snapshot = await getDocs(query(collection(db, 'SalaryPayments'), where('employeeId', '==', id)));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } else if (section === 'leaveRequests') {
      const snapshot = await getDocs(query(collection(db, 'EmployeeLeaveRequests'), where('employeeId', '==', id)));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } else if (section === 'checkIns') {
      const snapshot = await getDocs(query(collection(db, 'EmployeeCheckIns'), where('employeeId', '==', id)));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } else if (section === 'disciplinaryRecords') {
      const snapshot = await getDocs(query(collection(db, 'EmployeeDisciplinaryRecords'), where('employeeId', '==', id)));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } else if (section === 'teachingSessions') {
      const snapshot = await getDocs(query(collection(db, 'allclasses'), where('instructor', '==', id)));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    } else if (section === 'activityLogs') {
      const snapshot = await getDocs(collection(db, 'AdminActivity'));
      nextRecords = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    }

    nextRecords.sort((first, second) => {
      const secondTime = toTimestampNumber(
        second.paymentDate
        || second.createdAt
        || second.checkInAt
        || second.incidentDate
        || second.timeCreated
        || second.startDate
      );
      const firstTime = toTimestampNumber(
        first.paymentDate
        || first.createdAt
        || first.checkInAt
        || first.incidentDate
        || first.timeCreated
        || first.startDate
      );
      return secondTime - firstTime;
    });

    setRecords(nextRecords);
  }, [config, db, id, section]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadEmployeeSection();
      } catch (error) {
        console.error('Failed to load employee section:', error);
        swal('Error', 'This employee workspace could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadEmployeeSection]);

  useEffect(() => {
    setSectionForm(createSectionFormState(section));
    setSalaryForm(buildSalaryFormState());
    setSearchTerm('');
  }, [section]);

  useEffect(() => {
    if (!employee || !config || hasLoggedViewRef.current) return;

    logAdminActivity({
      action: 'Viewed Employee Section',
      details: `Opened ${config.title.toLowerCase()} for ${employee.employeeName || 'employee'}`,
      meta: {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        employeeSection: section
      }
    });

    hasLoggedViewRef.current = true;
  }, [config, employee, section]);

  useEffect(() => {
    hasLoggedViewRef.current = false;
  }, [section, id]);

  const filteredRecords = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return records.filter((record) => {
      if (section === 'salaryPayments') {
        return matchesSearch(queryText, [
          record.salaryPeriodLabel,
          record.method,
          record.reference,
          record.notes,
          String(record.amount || '')
        ]);
      }

      if (section === 'leaveRequests') {
        return matchesSearch(queryText, [
          record.leaveType,
          record.status,
          record.approverName,
          record.reason,
          record.startDate,
          record.endDate
        ]);
      }

      if (section === 'checkIns') {
        return matchesSearch(queryText, [
          record.attendanceStatus,
          record.location,
          record.shiftLabel,
          record.notes
        ]);
      }

      if (section === 'disciplinaryRecords') {
        return matchesSearch(queryText, [
          record.recordType,
          record.severity,
          record.status,
          record.issuedBy,
          record.summary,
          record.actionTaken
        ]);
      }

      if (section === 'teachingSessions') {
        return matchesSearch(queryText, [
          record.classDesc,
          record.campus,
          record.isActive,
          record.startDate,
          record.startTime,
          record.endTime,
          record.roomID
        ]);
      }

      if (section === 'activityLogs') {
        const employeeNeedles = [
          String(employee?.id || '').trim().toLowerCase(),
          String(employee?.employeeName || '').trim().toLowerCase(),
          String(employee?.employeeEmail || '').trim().toLowerCase(),
          String(employee?.currentEmail || '').trim().toLowerCase()
        ].filter(Boolean);

        const haystack = [
          record.employeeId,
          record.employeeName,
          record.action,
          record.details,
          record.path,
          record.page,
          record.adminName
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const belongsToEmployee = (
          String(record.employeeId || '').trim() === String(employee?.id || '').trim()
          || employeeNeedles.some((needle) => haystack.includes(needle))
        );

        if (!belongsToEmployee) return false;
        return matchesSearch(queryText, [haystack]);
      }

      return true;
    });
  }, [employee, records, searchTerm, section]);

  const salaryAmount = getEmployeeSalaryForPeriod(employee || {}, periodKey);
  const currentPeriodLabel = getMonthLabel(periodKey);
  const salaryPeriodPayments = useMemo(() => {
    if (section !== 'salaryPayments') return [];

    return records.filter((payment) => {
      const paymentPeriodKey = payment.salaryPeriodKey || getMonthKey(payment.paymentDate);
      return paymentPeriodKey === periodKey;
    });
  }, [periodKey, records, section]);

  const periodPaid = salaryPeriodPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const remainingSalary = Math.max(salaryAmount - periodPaid, 0);
  const allTimePaid = records.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const salaryProgress = salaryAmount > 0 ? Math.min((periodPaid / salaryAmount) * 100, 100) : 0;
  const paymentTone = remainingSalary <= 0 && salaryAmount > 0 ? 'success' : periodPaid > 0 ? 'warning' : 'danger';
  const paymentStatus =
    salaryAmount <= 0 ? 'Amount not set' : remainingSalary <= 0 ? 'Settled' : periodPaid > 0 ? 'Partially paid' : 'Not paid';

  const stats = useMemo(() => {
    if (section === 'salaryPayments') {
      return [
        { label: 'Salary amount', value: formatKes(salaryAmount) },
        { label: 'Paid this period', value: formatKes(periodPaid) },
        { label: 'Remaining', value: formatKes(remainingSalary) },
        { label: 'All-time paid', value: formatKes(allTimePaid) }
      ];
    }

    if (section === 'leaveRequests') {
      const pending = records.filter((item) => String(item.status || '').trim() === 'Pending').length;
      const approved = records.filter((item) => String(item.status || '').trim() === 'Approved').length;
      const rejected = records.filter((item) => String(item.status || '').trim() === 'Rejected').length;
      return [
        { label: 'Total requests', value: String(filteredRecords.length) },
        { label: 'Pending', value: String(pending) },
        { label: 'Approved', value: String(approved) },
        { label: 'Rejected', value: String(rejected) }
      ];
    }

    if (section === 'checkIns') {
      const present = records.filter((item) => item.attendanceStatus === 'Present').length;
      const late = records.filter((item) => item.attendanceStatus === 'Late').length;
      const missed = records.filter((item) => item.attendanceStatus === 'Missed').length;
      return [
        { label: 'Entries', value: String(filteredRecords.length) },
        { label: 'Present', value: String(present) },
        { label: 'Late', value: String(late) },
        { label: 'Missed', value: String(missed) }
      ];
    }

    if (section === 'disciplinaryRecords') {
      const warnings = records.filter((item) => item.recordType === 'Warning').length;
      const active = records.filter((item) => item.status === 'Active').length;
      const resolved = records.filter((item) => item.status === 'Resolved').length;
      return [
        { label: 'Records', value: String(filteredRecords.length) },
        { label: 'Warnings', value: String(warnings) },
        { label: 'Active', value: String(active) },
        { label: 'Resolved', value: String(resolved) }
      ];
    }

    if (section === 'activityLogs') {
      const relatedLogs = filteredRecords;
      const uniqueAdmins = new Set(relatedLogs.map((item) => item.adminId).filter(Boolean));
      const views = relatedLogs.filter((item) => String(item.action || '').toLowerCase().includes('view')).length;
      const changes = relatedLogs.filter((item) => String(item.action || '').toLowerCase().includes('update')).length;
      return [
        { label: 'Logs linked', value: String(relatedLogs.length) },
        { label: 'Admins', value: String(uniqueAdmins.size) },
        { label: 'Views', value: String(views) },
        { label: 'Updates', value: String(changes) }
      ];
    }

    if (section === 'teachingSessions') {
      const activeClasses = records.filter((item) => String(item.isActive || '').trim() === 'Active').length;
      return [
        { label: 'Classes', value: String(filteredRecords.length) },
        { label: 'Active', value: String(activeClasses) },
        { label: 'Campus', value: employee?.campus || 'Not set' },
        { label: 'Role', value: employee?.jobPosition || 'Not set' }
      ];
    }

    return [
      { label: 'Employee', value: employee?.employeeName || 'Employee' },
      { label: 'Campus', value: employee?.campus || 'Not set' },
      { label: 'Role', value: employee?.jobPosition || 'Not set' },
      { label: 'Status', value: normalizeEmployeeStatus(employee?.employeeStatus) }
    ];
  }, [allTimePaid, employee?.campus, employee?.employeeName, employee?.employeeStatus, employee?.jobPosition, filteredRecords, periodPaid, records, remainingSalary, salaryAmount, section]);

  const handleSimpleFieldChange = (event) => {
    const { name, value } = event.target;
    setSectionForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handleSalaryFieldChange = (event) => {
    const { name, value } = event.target;
    setSalaryForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const saveSalaryPayment = async () => {
    if (!employee?.id) return;

    if (!auth.currentUser) {
      swal('Error', 'You need to be signed in to record salary payments.', 'error');
      return;
    }

    const amountValue = Number(salaryForm.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (amountValue > remainingSalary) {
      swal('Amount too high', `You can only pay up to ${formatKes(remainingSalary)} for ${currentPeriodLabel}.`, 'warning');
      return;
    }

    const paymentDateValue = new Date(salaryForm.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const salaryPaymentRef = doc(collection(db, 'SalaryPayments'));
      await setDoc(salaryPaymentRef, {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        campus: employee.campus || '',
        jobPosition: employee.jobPosition || '',
        employeeStatus: employee.employeeStatus || 'Active',
        amount: amountValue,
        paymentDate: Timestamp.fromDate(paymentDateValue),
        method: salaryForm.method,
        reference: salaryForm.reference.trim(),
        notes: salaryForm.notes.trim(),
        salaryPeriodKey: periodKey,
        salaryPeriodLabel: currentPeriodLabel,
        expectedSalary: salaryAmount,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.uid || '',
        docId: salaryPaymentRef.id
      });

      await logAdminActivity({
        action: 'Recorded Salary Payment',
        details: `Recorded ${formatKes(amountValue)} for ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeSection: 'salaryPayments'
        }
      });

      swal('Success', 'Salary payment recorded successfully.', 'success');
      setSalaryForm(buildSalaryFormState());
      await loadEmployeeSection();
    } catch (error) {
      console.error('Failed to record salary payment:', error);
      swal('Error', 'Failed to record salary payment.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveLeaveRequest = async () => {
    if (!employee?.id) return;

    const startDate = String(sectionForm.startDate || '').trim();
    const endDate = String(sectionForm.endDate || '').trim();
    const daysRequested = calculateDateRangeDays(startDate, endDate);

    if (!startDate || !endDate || daysRequested <= 0) {
      swal('Invalid dates', 'Choose a valid leave date range.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const leaveRef = doc(collection(db, 'EmployeeLeaveRequests'));
      await setDoc(leaveRef, {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        campus: employee.campus || '',
        leaveType: sectionForm.leaveType,
        startDate,
        endDate,
        daysRequested,
        status: sectionForm.status,
        approverName: sectionForm.approverName.trim(),
        reason: sectionForm.reason.trim(),
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.uid || '',
        docId: leaveRef.id
      });

      await logAdminActivity({
        action: 'Added Employee Leave Request',
        details: `Saved leave request for ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeSection: 'leaveRequests'
        }
      });

      swal('Saved', 'Leave request recorded successfully.', 'success');
      setSectionForm(createSectionFormState(section));
      await loadEmployeeSection();
    } catch (error) {
      console.error('Failed to record leave request:', error);
      swal('Error', 'Failed to record leave request.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveCheckIn = async () => {
    if (!employee?.id) return;

    const checkInDate = new Date(sectionForm.checkInAt);
    const checkOutDate = sectionForm.checkOutAt ? new Date(sectionForm.checkOutAt) : null;

    if (Number.isNaN(checkInDate.getTime())) {
      swal('Invalid time', 'Please choose a valid check-in time.', 'warning');
      return;
    }

    if (checkOutDate && Number.isNaN(checkOutDate.getTime())) {
      swal('Invalid time', 'Please choose a valid check-out time.', 'warning');
      return;
    }

    if (checkOutDate && checkOutDate < checkInDate) {
      swal('Invalid time', 'Check-out time cannot be earlier than check-in time.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const checkInRef = doc(collection(db, 'EmployeeCheckIns'));
      await setDoc(checkInRef, {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        campus: employee.campus || '',
        attendanceStatus: sectionForm.attendanceStatus,
        location: sectionForm.location.trim(),
        shiftLabel: sectionForm.shiftLabel.trim(),
        notes: sectionForm.notes.trim(),
        checkInAt: Timestamp.fromDate(checkInDate),
        checkOutAt: checkOutDate ? Timestamp.fromDate(checkOutDate) : null,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.uid || '',
        docId: checkInRef.id
      });

      await logAdminActivity({
        action: 'Added Employee Check-in',
        details: `Saved attendance record for ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeSection: 'checkIns'
        }
      });

      swal('Saved', 'Check-in record saved successfully.', 'success');
      setSectionForm(createSectionFormState(section));
      await loadEmployeeSection();
    } catch (error) {
      console.error('Failed to save check-in record:', error);
      swal('Error', 'Failed to save check-in record.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveDisciplinaryRecord = async () => {
    if (!employee?.id) return;

    if (!String(sectionForm.incidentDate || '').trim() || !String(sectionForm.summary || '').trim()) {
      swal('Missing details', 'Incident date and summary are required.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const recordRef = doc(collection(db, 'EmployeeDisciplinaryRecords'));
      await setDoc(recordRef, {
        employeeId: employee.id,
        employeeName: employee.employeeName || '',
        campus: employee.campus || '',
        recordType: sectionForm.recordType,
        severity: sectionForm.severity,
        incidentDate: sectionForm.incidentDate,
        status: sectionForm.status,
        issuedBy: sectionForm.issuedBy.trim(),
        summary: sectionForm.summary.trim(),
        actionTaken: sectionForm.actionTaken.trim(),
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.uid || '',
        docId: recordRef.id
      });

      await logAdminActivity({
        action: 'Added Employee Disciplinary Record',
        details: `Saved ${sectionForm.recordType.toLowerCase()} record for ${employee.employeeName || 'employee'}`,
        meta: {
          employeeId: employee.id,
          employeeName: employee.employeeName || '',
          employeeSection: 'disciplinaryRecords'
        }
      });

      swal('Saved', 'Disciplinary record saved successfully.', 'success');
      setSectionForm(createSectionFormState(section));
      await loadEmployeeSection();
    } catch (error) {
      console.error('Failed to save disciplinary record:', error);
      swal('Error', 'Failed to save disciplinary record.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (section === 'salaryPayments') {
      await saveSalaryPayment();
      return;
    }

    if (section === 'leaveRequests') {
      await saveLeaveRequest();
      return;
    }

    if (section === 'checkIns') {
      await saveCheckIn();
      return;
    }

    if (section === 'disciplinaryRecords') {
      await saveDisciplinaryRecord();
    }
  };

  const renderTableRows = () => {
    if (section === 'salaryPayments') {
      return filteredRecords.map((payment) => (
        <tr key={payment.id || payment.docId}>
          <td data-label="Payment date">{formatDateTime(payment.paymentDate)}</td>
          <td data-label="Applied month">{payment.salaryPeriodLabel || getMonthLabel(payment.salaryPeriodKey || getMonthKey(payment.paymentDate))}</td>
          <td data-label="Amount">{formatKes(payment.amount)}</td>
          <td data-label="Method">{payment.method || '—'}</td>
          <td data-label="Reference">{payment.reference || '—'}</td>
          <td data-label="Notes">{payment.notes || '—'}</td>
        </tr>
      ));
    }

    if (section === 'leaveRequests') {
      return filteredRecords.map((item) => (
        <tr key={item.id || item.docId}>
          <td data-label="Requested">{formatDateTime(item.createdAt)}</td>
          <td data-label="Leave type">{item.leaveType || '—'}</td>
          <td data-label="Dates">{item.startDate || '—'} to {item.endDate || '—'}</td>
          <td data-label="Days">{item.daysRequested || 0}</td>
          <td data-label="Status"><span className={`employeeStatusBadge ${String(item.status || '').toLowerCase()}`}>{item.status || 'Pending'}</span></td>
          <td data-label="Reason">{item.reason || '—'}</td>
        </tr>
      ));
    }

    if (section === 'checkIns') {
      return filteredRecords.map((item) => (
        <tr key={item.id || item.docId}>
          <td data-label="Check in">{formatDateTime(item.checkInAt)}</td>
          <td data-label="Check out">{formatDateTime(item.checkOutAt)}</td>
          <td data-label="Status">{item.attendanceStatus || '—'}</td>
          <td data-label="Location">{item.location || '—'}</td>
          <td data-label="Shift">{item.shiftLabel || '—'}</td>
          <td data-label="Notes">{item.notes || '—'}</td>
        </tr>
      ));
    }

    if (section === 'disciplinaryRecords') {
      return filteredRecords.map((item) => (
        <tr key={item.id || item.docId}>
          <td data-label="Incident date">{item.incidentDate || '—'}</td>
          <td data-label="Type">{item.recordType || '—'}</td>
          <td data-label="Severity">{item.severity || '—'}</td>
          <td data-label="Status">{item.status || '—'}</td>
          <td data-label="Issued by">{item.issuedBy || '—'}</td>
          <td data-label="Summary">{item.summary || '—'}</td>
        </tr>
      ));
    }

    if (section === 'teachingSessions') {
      return filteredRecords.map((item) => (
        <tr key={item.id || item.roomID}>
          <td data-label="Class">{item.classDesc || 'Class'}</td>
          <td data-label="Campus">{item.campus || employee?.campus || '—'}</td>
          <td data-label="Start">{item.startDate || '—'}</td>
          <td data-label="Time">{item.startTime || '—'} - {item.endTime || '—'}</td>
          <td data-label="Status">{item.isActive || '—'}</td>
          <td data-label="Room">{item.roomID || '—'}</td>
        </tr>
      ));
    }

    if (section === 'activityLogs') {
      return filteredRecords.map((item) => (
        <tr key={item.id}>
          <td data-label="Logged at">{formatDateTime(item.createdAt)}</td>
          <td data-label="Admin">{item.adminName || 'Admin'}</td>
          <td data-label="Action">{item.action || 'Activity'}</td>
          <td data-label="Details">{item.details || '—'}</td>
          <td data-label="Page">{item.page || item.path || '—'}</td>
        </tr>
      ));
    }

    return null;
  };

  const renderTableHead = () => {
    if (section === 'salaryPayments') {
      return (
        <tr>
          <th>Payment date</th>
          <th>Applied month</th>
          <th>Amount</th>
          <th>Method</th>
          <th>Reference</th>
          <th>Notes</th>
        </tr>
      );
    }

    if (section === 'leaveRequests') {
      return (
        <tr>
          <th>Requested</th>
          <th>Leave type</th>
          <th>Dates</th>
          <th>Days</th>
          <th>Status</th>
          <th>Reason</th>
        </tr>
      );
    }

    if (section === 'checkIns') {
      return (
        <tr>
          <th>Check in</th>
          <th>Check out</th>
          <th>Status</th>
          <th>Location</th>
          <th>Shift</th>
          <th>Notes</th>
        </tr>
      );
    }

    if (section === 'disciplinaryRecords') {
      return (
        <tr>
          <th>Incident date</th>
          <th>Type</th>
          <th>Severity</th>
          <th>Status</th>
          <th>Issued by</th>
          <th>Summary</th>
        </tr>
      );
    }

    if (section === 'teachingSessions') {
      return (
        <tr>
          <th>Class</th>
          <th>Campus</th>
          <th>Start</th>
          <th>Time</th>
          <th>Status</th>
          <th>Room</th>
        </tr>
      );
    }

    return (
      <tr>
        <th>Logged at</th>
        <th>Admin</th>
        <th>Action</th>
        <th>Details</th>
        <th>Page</th>
      </tr>
    );
  };

  const renderSidebarPrimaryCard = () => {
    if (section === 'salaryPayments') {
      return null;
    }

    if (section === 'leaveRequests') {
      return (
        <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>Add leave request</h4>
              <p>Capture the leave type, dates, decision status, and reason from one panel.</p>
            </div>
          </div>

          <div className="employeeManagementFormGrid">
            <Form.Group className="mb-3">
              <Form.Label>Leave type</Form.Label>
              <Form.Select name="leaveType" value={sectionForm.leaveType} onChange={handleSimpleFieldChange}>
                <option value="Annual Leave">Annual Leave</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Compassionate Leave">Compassionate Leave</option>
                <option value="Study Leave">Study Leave</option>
                <option value="Emergency Leave">Emergency Leave</option>
                <option value="Unpaid Leave">Unpaid Leave</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start date</Form.Label>
              <Form.Control type="date" name="startDate" value={sectionForm.startDate} onChange={handleSimpleFieldChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End date</Form.Label>
              <Form.Control type="date" name="endDate" value={sectionForm.endDate} onChange={handleSimpleFieldChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" value={sectionForm.status} onChange={handleSimpleFieldChange}>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Completed">Completed</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Approver or supervisor</Form.Label>
              <Form.Control name="approverName" value={sectionForm.approverName} onChange={handleSimpleFieldChange} placeholder="Approver name" />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Reason</Form.Label>
              <Form.Control as="textarea" rows={3} name="reason" value={sectionForm.reason} onChange={handleSimpleFieldChange} placeholder="Reason for leave" />
            </Form.Group>
          </div>

          <Button variant="dark" className="w-100" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save leave request'
            )}
          </Button>
        </div>
      );
    }

    if (section === 'checkIns') {
      return (
        <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>Add check-in record</h4>
              <p>Manual attendance entries help when you need a clean HR record outside a live clock system.</p>
            </div>
          </div>

          <div className="employeeManagementFormGrid">
            <Form.Group className="mb-3">
              <Form.Label>Check-in time</Form.Label>
              <Form.Control type="datetime-local" name="checkInAt" value={sectionForm.checkInAt} onChange={handleSimpleFieldChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Check-out time</Form.Label>
              <Form.Control type="datetime-local" name="checkOutAt" value={sectionForm.checkOutAt} onChange={handleSimpleFieldChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select name="attendanceStatus" value={sectionForm.attendanceStatus} onChange={handleSimpleFieldChange}>
                <option value="Present">Present</option>
                <option value="Late">Late</option>
                <option value="Remote">Remote</option>
                <option value="Missed">Missed</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control name="location" value={sectionForm.location} onChange={handleSimpleFieldChange} placeholder="Campus or work location" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Shift or session</Form.Label>
              <Form.Control name="shiftLabel" value={sectionForm.shiftLabel} onChange={handleSimpleFieldChange} placeholder="Morning shift, class support, remote..." />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={3} name="notes" value={sectionForm.notes} onChange={handleSimpleFieldChange} placeholder="Optional attendance note" />
            </Form.Group>
          </div>

          <Button variant="dark" className="w-100" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save check-in record'
            )}
          </Button>
        </div>
      );
    }

    if (section === 'disciplinaryRecords') {
      return (
        <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>Add disciplinary record</h4>
              <p>Keep warnings and conduct records consistent so follow-up decisions stay traceable.</p>
            </div>
          </div>

          <div className="employeeManagementFormGrid">
            <Form.Group className="mb-3">
              <Form.Label>Record type</Form.Label>
              <Form.Select name="recordType" value={sectionForm.recordType} onChange={handleSimpleFieldChange}>
                <option value="Warning">Warning</option>
                <option value="Disciplinary">Disciplinary</option>
                <option value="Performance">Performance</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Severity</Form.Label>
              <Form.Select name="severity" value={sectionForm.severity} onChange={handleSimpleFieldChange}>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Incident date</Form.Label>
              <Form.Control type="date" name="incidentDate" value={sectionForm.incidentDate} onChange={handleSimpleFieldChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" value={sectionForm.status} onChange={handleSimpleFieldChange}>
                <option value="Active">Active</option>
                <option value="Monitoring">Monitoring</option>
                <option value="Resolved">Resolved</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Issued by</Form.Label>
              <Form.Control name="issuedBy" value={sectionForm.issuedBy} onChange={handleSimpleFieldChange} placeholder="Manager or HR lead" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control as="textarea" rows={3} name="summary" value={sectionForm.summary} onChange={handleSimpleFieldChange} placeholder="What happened?" />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Action taken</Form.Label>
              <Form.Control as="textarea" rows={3} name="actionTaken" value={sectionForm.actionTaken} onChange={handleSimpleFieldChange} placeholder="Follow-up action or expected next step" />
            </Form.Group>
          </div>

          <Button variant="dark" className="w-100" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save record'
            )}
          </Button>
        </div>
      );
    }

    if (section === 'teachingSessions') {
      return (
        <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>Teaching summary</h4>
              <p>This view pulls classes from the Darasa `allclasses` records where this employee is the assigned instructor.</p>
            </div>
          </div>
          <div className="employeeManagementMiniMetrics employeeSectionMiniMetrics">
            <div className="employeeManagementMiniMetric">
              <span>Employee role</span>
              <strong>{employee?.jobPosition || 'Not set'}</strong>
            </div>
            <div className="employeeManagementMiniMetric">
              <span>Instructor profile</span>
              <strong>{String(employee?.jobPosition || '').trim() === 'Instructor' ? 'Available' : 'Not linked'}</strong>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
        <div className="employeeProfilePanelHeader">
          <div>
            <h4>Activity summary</h4>
            <p>New employee management actions now log employee metadata so this audit trail becomes more useful over time.</p>
          </div>
        </div>
        <div className="employeeManagementMiniMetrics employeeSectionMiniMetrics">
          <div className="employeeManagementMiniMetric">
            <span>Employee tracked</span>
            <strong>{employee?.employeeName || 'Employee'}</strong>
          </div>
          <div className="employeeManagementMiniMetric">
            <span>Linked by</span>
            <strong>ID and profile text</strong>
          </div>
        </div>
      </div>
    );
  };

  if (!config) {
    return null;
  }

  if (loading) {
    return (
      <div className="employeeDirectoryPage employeeProfileViewPage">
        <div className="employeeDirectoryPanel employeeProfilePanel">
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading employee workspace...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="employeeDirectoryPage employeeProfileViewPage">
        <div className="employeeDirectoryPanel employeeProfilePanel">
          <div className="opsEmptyState">
            <h6>Employee not found</h6>
            <p>The employee workspace you are looking for could not be found.</p>
            <button type="button" className="employeeProfileBackBtn" onClick={() => navigate('/employees')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to employees
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employeeDirectoryPage employeeProfileViewPage employeeSectionWorkspacePage">
      <section className="employeeProfileHero">
        <div className="employeeProfileHeroTop employeeSectionTopRow">
          <button type="button" className="employeeProfileBackBtn" onClick={() => navigate(`/view-employee/${employee.id}`)}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to employee</span>
          </button>
        </div>

        <div className="employeeProfileHeroBody">
          <div className="employeeProfileHeroCopy">
            <p className="employeeDirectoryEyebrow">{config.eyebrow}</p>
            <div className="employeeProfileTitleRow">
              <h3>{config.title}</h3>
              <span className={`employeeStatusBadge ${getEmployeeStatusClass(employee.employeeStatus)}`}>
                {normalizeEmployeeStatus(employee.employeeStatus)}
              </span>
            </div>
            <p className="employeeProfileLead">{config.description}</p>
            <div className="employeeDirectoryHeroMeta">
              <span>{employee.employeeName || 'Employee'}</span>
              <span>{employee.jobPosition || 'Role not set'}</span>
              <span>{employee.campus || 'Campus not set'}</span>
              <span>{employee.employeeEmail || 'Email not set'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="employeeDirectoryStats employeeProfileStats employeeManagementHubStats">
        {stats.map((item) => (
          <article key={item.label} className="employeeDirectoryStatCard">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </article>
        ))}
      </section>

      <section className="employeeSectionWorkspaceGrid">
        <article className="employeeDirectoryPanel employeeProfilePanel employeeSectionMainPanel">
          <div className="employeeProfilePanelHeader">
            <div>
              <h4>{config.title}</h4>
              <p>{config.emptyMessage}</p>
            </div>
            <div className="employeeSectionToolbar">
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={config.searchPlaceholder}
              />
              {section === 'salaryPayments' && (
                <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
              )}
            </div>
          </div>

          <div className="appsTableCard employeeProfileTableCard employeeSectionTableCard">
            <div className="appsTableHeader">
              <h6>{config.title}</h6>
              <span>{filteredRecords.length} records</span>
            </div>
            <div className="appsTableWrap">
              <Table hover responsive className="appsTable">
                <thead>{renderTableHead()}</thead>
                <tbody>
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="appsEmptyState">
                        {config.emptyTitle}
                      </td>
                    </tr>
                  ) : (
                    renderTableRows()
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </article>

        <aside className="employeeSectionSidebar">
          {renderSidebarPrimaryCard()}

          <div className="employeeDirectoryPanel employeeProfilePanel employeeSectionSideCard">
            <div className="employeeProfileMiniBlock employeeProfileMiniBlock--flush">
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faEnvelope} /> Work email</span>
                <strong>{employee.employeeEmail || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faPhone} /> Phone</span>
                <strong>{employee.employeePhone || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faBuilding} /> Campus</span>
                <strong>{employee.campus || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faCoins} /> Salary</span>
                <strong>{formatKes(getEmployeeSalaryForPeriod(employee || {}, periodKey))}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faIdCard} /> NSSF</span>
                <strong>{employee.nssfNumber || 'Not set'}</strong>
              </div>
              <div className="employeeProfileMiniRow">
                <span><FontAwesomeIcon icon={faBriefcase} /> Payment method</span>
                <strong>{employee.salaryPaymentMethod || 'Not set'}</strong>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

export default EmployeeSectionComp;
